import { CreditCardTracking } from '../../helpers/credit-card-tracking';
import { initStripeLibrary, stripe } from './stripeInterface';
import { barkApi } from '@bark/bark-core/src/components/legacy-scripts/barklib';
import { _t, tracking } from '@bark/bark-core';
import { getFullLocale } from "@bark/bark-core/src/helpers";
import {expressCheckoutSuccessHandler, getReturnUrl} from "../../helpers/payments/wallet-payments";
import {setDefaultPaymentMethod} from "../../helpers/payments/helpers";

/**
 * @typedef StripeToken
 * @property {string} id
 */

/**
 * @typedef TrackableParameters
 * @property {string|undefined} trk_current_bes_token
 * @property {string|undefined} trk_estimated_zip
 * @property {string|undefined} trk_estimated_city
 * @property {string|undefined} trk_estimated_country_code
 * @property {string|undefined} trk_estimated_isp
 * @property {string|undefined} trk_metric_accounts
 * @property {string|undefined} trk_metric_sources
 */

/** @type {{cardNumber: StripeCardNumberElement, cardExpiry: StripeCardExpiryElement, cardCvc: StripeCardCvcElement}} */
let stripeCardElements = {};
let vatMultiplierForDisplay = null;
let stripeLoaded;
let setupIntent = { promise: null, id: null, supportsIntents: true };
let creditCardTracking = CreditCardTracking();

const SAVED_CARD = 'saved-card';
const SOFORT_PAYMENT = 'sofort';
const NEW_CARD = 'new-card';

const multiPaymentModalSelector = '.js-multiple-payment-modal';
const isMultiPaymentModal = () =>  {
    return document.getElementById('multi-payment-modal');
}
let stripeElementsCompleteState = {
    cardNumberIsComplete: false,
    expiryDateIsComplete: false,
    cvcIsComplete: false,
};
let lastAttemptedPaymentMethodType;



/**
 *
 * @param {string} jQueryModalSelector Selector for the modal
 * @param {paymentSuccessCallback} successCallback Called when the user has successfully created a token for the card and the modal is closed
 * @param {function} [cancelCallback] Called when the user clicks the "cancel" action and the modal is closed
 * @param {function} [closeCallback] Called when the user clicks the close button and the modal is closed
 * @param {{headerText?: string, subheaderHtml?: string, formFooterHtml?: string, confirmText?: string, cancelText?: string, clearModal?: boolean}} [options]
 */
export const initCardModal = (
	jQueryModalSelector,
	successCallback,
	cancelCallback,
	closeCallback,
) => {
	const $modal = $(jQueryModalSelector);
	if (!stripe) {
		stripeLoaded = initStripeLibrary($modal.data('user-id'));
	}

	$modal
		.find('.js-card-modal-confirm')
		.off('click.confirm')
		.on('click.confirm', handleConfirmClick($modal, successCallback));
	$modal
		.find('.js-card-modal-cancel')
		.off('click.cancel')
		.on('click.cancel', handleCancelClick($modal, cancelCallback));
	$modal
		.find('.js-close-card-payment-form')
		.off('click.cancel')
		.on('click.cancel', handleCloseClick($modal, closeCallback));

	setHeaderText($modal, _t('seller_common:card-form.default-title'));
	setSubheaderHtml($modal, '');
	setFormFooterHtml($modal, '');
	setConfirmText($modal, _t('seller_common:card-form.purchase'));
	setCancelText($modal, _t('seller_common:card-form.cancel'));
	clearModal($modal);

	return $modal;
};

export const initMultiPaymentCardModal = (
    jQueryModalSelector,
    successCallback,
    cancelCallback,
    closeCallback,
    savedCardCallback
) => {
    const $modal = $(jQueryModalSelector);
    resetStripeElementCompleteState()
    if (!stripe) {
        stripeLoaded = initStripeLibrary($modal.data('user-id'));
    }
    $modal
        .find('.js-payment-modal-confirm')
        .off('click.confirm')
        .on('click.confirm', handlePaymentClick($modal,  successCallback, savedCardCallback));
    $modal
        .find('.js-card-modal-cancel')
        .off('click.cancel')
        .on('click.cancel', handleCancelClick($modal, cancelCallback));
    $modal
        .find('.js-close-card-payment-form')
        .off('click.cancel')
        .on('click.cancel', handleCloseClick($modal, closeCallback));

    $('#payment-accordion .collapse').on('shown.bs.collapse', function (e) {
        $(e.target).closest('.accordion').find('.card-header').removeClass('open').addClass('closed');
        let $currentAccordionHeader = $("#" + $(e.target).attr('id') + 'Heading');
        $currentAccordionHeader.removeClass('closed').addClass('open')
        $currentAccordionHeader.find('input[type=radio]').prop('checked',true);

        setConfirmText(
            $modal,
            $currentAccordionHeader.data('confirm-payment-text'),
            '.js-payment-modal-confirm-text .body'
        );
        setConfirmPaymentIcon(
            $modal,
            $currentAccordionHeader.find('.payment-icon').html() ?? ""
        );
        setPaymentConfirmOption(
            $modal,
            { "data-payment-method": $currentAccordionHeader.data("payment-method") }
        );
        if(isMultiPaymentFormProbablyComplete() || getActiveAccordionSection() === SOFORT_PAYMENT || getActiveAccordionSection() === SAVED_CARD) {
            setMultiPaymentBuyButtonEnabledState(true);
        } else {
            setMultiPaymentBuyButtonEnabledState(false);
        }
    });

    $('#multi-payment-modal .js-card-header').off('click.tracking').on('click.tracking', ({ currentTarget, originalEvent }) => {
        const { paymentMethod: payment_method } = $(currentTarget).data();
        // Only send the tracking event if it was triggered by a human
        originalEvent && creditCardTracking.logAction('select payment method', {
            payment_method
        });
    });

    setHeaderText($modal, _t('seller_common:card-form.default-title'));
    setSubheaderHtml($modal, '');
    setFormFooterHtml($modal, '');
    setCancelText($modal, _t('seller_common:card-form.cancel'));
    clearModal($modal);

    return $modal;
};

const getActiveAccordionSection = () => {
    const activeId = $('#payment-accordion .open').attr('id');
    if(activeId === 'collapseSofortHeading') {
        return SOFORT_PAYMENT;
    }
    if(activeId === 'collapseNewCardHeading') {
        return NEW_CARD;
    }
    if(activeId === 'collapseSavedCardHeading') {
        return SAVED_CARD;
    }

    // fallback
    return NEW_CARD;
}

const setActiveAccordionSection = (section) => {
    if(section === SOFORT_PAYMENT) {
        $('#collapseSofortHeading')?.click()
        $('#collapseSofort')?.collapse('show')
    }
    if(section === NEW_CARD) {
        $('#collapseNewCardHeading')?.click()
        $('#collapseNewCard')?.collapse('show')
    }
    if(section === SAVED_CARD) {
        $('#collapseSavedCardHeading')?.click()
        $('#collapseSavedCard')?.collapse('show')
    }
}

/**
 * @param {object} options
 * @param {boolean?} options.enableWallets
 */
const shouldMountStripeElements = (options, $modal) => {
	if (options.enableWallets) {
		// Ensure that the amount displayed to a user by a payment request button is updated
		return true;
	}

	// Copied from elsewhere
	return typeof stripeCardElements['cardNumber'] == 'undefined' ||
		(
			stripeCardElements['cardNumber']?._getParent() !== undefined
			&& !$modal.find(stripeCardElements['cardNumber']._getParent()).length
		);
};

/**
 * @param $modal
 * @param {object} options
 * @param {string?} options.headerText
 * @param {string?} options.subheaderHtml
 * @param {string?} options.formFooterHtml
 * @param {string?} options.confirmText
 * @param {string?} options.cancelText
 * @param {boolean?} options.clearModal
 * @param {boolean?} options.resetFormAfterFailedSCA
 * @param {boolean?} options.enableWallets True to enable Apple Pay or Google Pay. paymentOptions is also required to be set
 * @param {PaymentRequestOptions?} options.paymentOptions Required if enabling wallets.
 */
export const showMultiPaymentModal = ($modal, options) => {
    if (options) {
        const { headerText, subheaderHtml, formFooterHtml, confirmText, cancelText } =
            options;
        headerText !== undefined && setHeaderText($modal, headerText, '.js-modal-multi-title-text');
        subheaderHtml !== undefined && setSubheaderHtml($modal, subheaderHtml, ".js-modal-multi-subheader-html");
        formFooterHtml !== undefined && setFormFooterHtml($modal, formFooterHtml);
        confirmText !== undefined && setConfirmText($modal, confirmText);
        cancelText !== undefined && setCancelText($modal, cancelText);
        options['clearModal'] && clearModal($modal);
        options['resetFormAfterFailedSCA'] && setFormStateAfterFailedSCAPayment()
    }

    showLoadingModalMultiPaymentForm($modal);
    resetStripeElementCompleteState()
    stripeLoaded
        .then(async () => {
			if (shouldMountStripeElements({enableWallets: options?.enableWallets}, $modal)) {
                await mountStripeElements($modal, {
					enableWallets: options?.enableWallets,
					paymentOptions: options?.paymentOptions,
                    style: {
                        fontSize: "14px"
                    }
				});
            }
            showMultiModalPaymentForm($modal);
        })
        .catch((e) => {
            $modal.modal('hide');
			$('.modal-backdrop').removeClass('modal-backdrop-blue');
            window.Bark.alertModal(
                _t('seller_common:card-form.error.loading-payment-provider'),
            );
        });
    if(getActiveAccordionSection() === SAVED_CARD || getActiveAccordionSection() === SOFORT_PAYMENT) { setMultiPaymentBuyButtonEnabledState(true)}
    $modal.modal('show');
    creditCardTracking.logAction('payment option');
}

/**
 *
 * @param $modal
 * @param {object} options
 * @param {string?} options.headerText
 * @param {string?} options.subheaderHtml
 * @param {string?} options.formFooterHtml
 * @param {string?} options.confirmText
 * @param {string?} options.cancelText
 * @param {boolean?} options.clearModal
 * @param {boolean?} options.isOnlySavingCard
 * @param {boolean?} options.enableWallets True to enable ApplePay or GooglePay. paymentOptions is also required to be set
 * @param {PaymentRequestOptions?} options.paymentOptions Required if enabling wallets.
 */
export const showCardModal = ($modal, options ) => {
	if (options) {
		const { headerText, subheaderHtml, formFooterHtml, confirmText, cancelText } =
			options;
		headerText !== undefined && setHeaderText($modal, headerText);
		subheaderHtml !== undefined && setSubheaderHtml($modal, subheaderHtml);
		formFooterHtml !== undefined && setFormFooterHtml($modal, formFooterHtml);
		confirmText !== undefined && setConfirmText($modal, confirmText);
		cancelText !== undefined && setCancelText($modal, cancelText);

		if (options.isOnlySavingCard) {
			$modal.addClass('saving-card');
		} else {
			$modal.removeClass('saving-card');
		}

		options['clearModal'] && clearModal($modal);
	} else {
		// As there are no options, assume that we are not saving a card
		$modal.removeClass('saving-card');
	}

	// stripe must be loaded before we show the modal

	showLoadingModalPaymentForm($modal);

	stripeLoaded
		.then(async () => {
			if (shouldMountStripeElements({enableWallets: options?.enableWallets}, $modal)) {
				await mountStripeElements($modal, {
					enableWallets: options?.enableWallets,
					paymentOptions: options?.paymentOptions,
				});
			}
			showModalPaymentForm($modal);
		})
		.catch((e) => {
			console.error(e);
			$modal.modal('hide');
			$('.modal-backdrop').removeClass('modal-backdrop-blue');
			window.Bark.alertModal(
				_t('seller_common:card-form.error.loading-payment-provider'),
			);
		});

	confirmButtonSetDisabled($modal, true);

	$modal.modal('show');

	$('.modal-backdrop').addClass('modal-backdrop-blue');

	creditCardTracking.logAction('open');
};

export const promptUserAction = (clientSecret, successCallback, failureCallback) => {
	stripeLoaded
		.then(() => {
			creditCardTracking.logAction('3ds-prompt-open');
			stripe
				.handleCardAction(clientSecret)
				.then((result) => {
					creditCardTracking.logAction(
						result?.paymentIntent ? '3ds-prompt-complete' : '3ds-prompt-failed',
					);
					successCallback(result);
				})
				.catch((err) => {
					window.bugsnagClient.notify(new Error('Failed to handle card action'), {
						metaData: { err: err, errText: '' + err },
					});
					failureCallback(err);
				});
		})
		.catch((err) => {
			window.Bark.alertModal(
				_t('seller_common:card-form.error.loading-payment-provider'),
			);
		});
};

const showModalPaymentForm = ($modal) => {
	confirmButtonSetDisabled($modal, true);

	$modal.find('.js-card-modal-payment-form').show();
	$modal.find('.js-card-modal-body-loading').hide();
};

const showMultiModalPaymentForm = ($modal) => {
    $modal.find('.js-modal-multi-payment-form').show();
    $modal.find('.js-modal-multi-body-loading').hide();
    $modal.find('.accordion > div:not(.d-none):first').addClass('round-card');
    $modal.find('.card-header').collapse()

    let defaultPaymentMethod = $modal.data("defaultPaymentMethod");
    if (defaultPaymentMethod){
        $modal.find(`.card-header[data-payment-method="${defaultPaymentMethod}"]`).trigger('click')
    } else {
        $modal.find('.card-header.default.collapsed').trigger('click');
    }
};

/**
 * @param $modal
 * @param elements
 * @param {PaymentRequestWalletsEnabled|PaymentRequestWalletsDisabled} options
 */
const togglePaymentRequestButton = async (elements, $modal, options) => {
	const paymentRequestContainer = $('.js-payment-request-button', $modal);
	const divider = $('.js-or-pay-with-card', $modal);
	const skeleton = $('.js-payment-request-skeleton', $modal);

    paymentRequestContainer.addClass('d-none');
	divider.addClass('d-none');
	skeleton.addClass('d-none');

	if (!options.enableWallets) {
		return;
	}

	skeleton.removeClass('d-none');

	try {
		const currency = $modal.data('currency');
		let chosenPaymentMethod = null;

		// Why Stripe did this, I do not know! Setting "off_session" here disables the Paypal button, but this is
		//	undocumented behaviour. It makes sense, but this property should be in the wallets dictionary
		const setupFutureUsage = options.paymentOptions?.disableWallets?.paypal ? 'off_session' : 'on_session';

        if (setupFutureUsage === 'off_session') {
            elements.update({setupFutureUsage: 'off_session'});
        }

		const expressCheckoutElement = elements.create('expressCheckout', {
			wallets: {
				applePay: options.paymentOptions?.disableWallets?.applePay ? 'never' : 'always',
				googlePay: options.paymentOptions?.disableWallets?.googlePay ? 'never' : 'always',
			},
			paymentMethodOrder: ['applePay', 'googlePay', 'paypal'],
            buttonType: {
                applePay: 'buy',
                googlePay: 'buy',
                paypal: 'pay'
            },
		});
		expressCheckoutElement.mount(paymentRequestContainer[0]);
		stripeCardElements['checkout'] = expressCheckoutElement;

		expressCheckoutElement.on('ready', (e) => {
			skeleton.addClass('d-none');

			if (e.availablePaymentMethods) {
				divider.removeClass('d-none');
				tracking('alternative-payments', 'Options available', {
					applePay: e.availablePaymentMethods?.applePay,
					googlePay: e.availablePaymentMethods?.googlePay,
					paypal: e.availablePaymentMethods?.paypal,
				});
			} else {
				paymentRequestContainer.addClass('d-none');
				tracking('alternative-payments', 'No options available');
			}
		});

        expressCheckoutElement.on('click', event => {
			hideModalErrorText($modal);

            tracking('alternative-payments', 'Click alternative payment', {
                type: event.expressPaymentType,
            });

			chosenPaymentMethod = event.expressPaymentType;

            if (['google_pay', 'apple_pay'].includes(event.expressPaymentType) && setupFutureUsage === 'on_session') {
                elements.update({setupFutureUsage: 'off_session'});
            }

            event.resolve();
        });

		expressCheckoutElement.on('confirm', async event => {
			await expressCheckoutSuccessHandler({
				$modal,
				chosenPaymentMethod,
				options,
				event,
				elements,
			})
		});
		paymentRequestContainer.removeClass('d-none');
	} catch (e) {
		console.error(e);
		window.bugsnagClient && window.bugsnagClient.notify(e)
		paymentRequestContainer.addClass('d-none');
	}
}

const showLoadingModalPaymentForm = ($modal) => {
	$modal.find('.js-card-modal-payment-form').hide();
	$modal.find('.js-card-modal-body-loading').show();
	confirmButtonSetDisabled($modal, true, '.js-payment-modal-confirm');
};

const showLoadingModalMultiPaymentForm = ($modal) => {
    $modal.find('.js-modal-multi-payment-form').hide();
    $modal.find('.js-modal-multi-body-loading').show();
    confirmButtonSetDisabled($modal, true, '.js-payment-modal-confirm');
};

const confirmButtonSetDisabled = ($modal, disabled, override = null) => {
	if (disabled) {
		$modal.find(override ?? '.js-card-modal-confirm').addClass('disabled');
	} else {
		$modal.find(override ?? '.js-card-modal-confirm').removeClass('disabled');
	}
};

const buttonIsDisabled = ($button) => {
	return $button.closest('.btn').hasClass('disabled');
};

const confirmButtonSetLoadingState = ($modal, loading, override = null) => {
	const $button = $modal.find(override ?? '.js-card-modal-confirm');
	if (loading) {
		$button.addClass('loading');
		$button.find('.js-loading-spinner').removeClass('d-none');
	} else {
		$button.removeClass('loading');
		$button.find('.js-loading-spinner').addClass('d-none');
	}
};

const buttonIsLoading = ($button) => {
	return $button.closest('.btn').hasClass('loading');
};

export const clearModal = ($modal) => {
	stripeCardElements = {};
	hideModalErrorText($modal);
};

/**
 * @param {Number} amountExVat
 * @param {1|2|3} attempt
 * @returns {Promise<Number>}
 */
const getVatMultiplierForDisplay = async (amountExVat, attempt = 1) => {
	if (vatMultiplierForDisplay) {
		return vatMultiplierForDisplay;
	}

	try {
		const result = await Bark.api('/transaction/tax/rate/', { transaction_amount_in_pence: Math.round(amountExVat) }, null, null, 'POST', Bark.apiVersionHeader('v2'));

		if (result.status) {
			vatMultiplierForDisplay = result.data.vat_multiplier;
			return vatMultiplierForDisplay;
		}

        window.bugsnagClient && window.bugsnagClient.notify(new Error('Failed to get tax multiplier'), {
            metaData: {
                amountExVat
            }
        });

		return 1;
	} catch (e) {
		if (attempt <= 3) {
			await getVatMultiplierForDisplay(amountExVat, attempt + 1);
		} else {
			throw e;
		}
	}
}

const toggleSubmitPaymentButton = ($modal) => {
	const allFieldsComplete = stripeElementsCompleteState.cvcIsComplete
		&& stripeElementsCompleteState.cardNumberIsComplete
		&& stripeElementsCompleteState.expiryDateIsComplete;

	confirmButtonSetDisabled(
		$modal,
		!allFieldsComplete
	);
};

/**
 * @param $modal
 * @param {object?} options
 * @param {object?} options.style
 * @param {string?} options.style.fontSize The font size to set on the Stripe Elements
 * @param {boolean?} options.enableWallets True to enable ApplePay or GooglePay. paymentOptions is also required to be set
 * @param {PaymentRequestOptions?} options.paymentOptions Required if enabling wallets.
 */
const mountStripeElements = async ($modal, options = {}) => {
	const walletOptions = {};

	if (options?.enableWallets) {
		// Always get VAT because this could change off page
		// if (vatMultiplierForDisplay === null)
        const taxFromModal = Number($modal.data('tax'));

        if (taxFromModal > 1) {
            vatMultiplierForDisplay = taxFromModal;
        } else {
            await getVatMultiplierForDisplay(options.paymentOptions.amount);
        }

		if (options.paymentOptions.isOnlySavingCard) {
			walletOptions.mode = 'setup';
		} else {
			walletOptions.mode = 'payment';
			walletOptions.amount = Math.floor(options.paymentOptions.amount * vatMultiplierForDisplay);
		}

		walletOptions.currency = $modal.data('currency')?.toLowerCase();
	}

	const elements = stripe.elements({
		fonts: [
			{
				family: 'gorditamedium',
				src: 'url(https://d1w7gvu0kpf6fl.cloudfront.net/fonts/gordita-medium-webfont.woff2)',
				style: 'normal',
				weight: 'normal',
			},
		],
        locale: getFullLocale(),
		...walletOptions,
	});

	/** @type StripeElementStyle */
	const style = {
		base: {
			fontFamily: 'gorditamedium',
			fontWeight: 'normal',
			fontSize: options?.style?.fontSize || '16px',
			color: '#111637',
			'::placeholder': {
				color: '#9da0b6',
				fontWeight: '100',
			},
		},
	};

	const modalId = `#${$modal.attr('id')}`;

	stripeCardElements = {};
	stripeCardElements['cardNumber'] = elements.create('cardNumber', {
		placeholder: '•••• •••• •••• ••••',
		style,
	});
	stripeCardElements['cardNumber'].on('focus', () =>
		creditCardTracking.logFocus('cc-number'),
	);
	stripeCardElements['cardNumber'].on(
		'change',
        (event, modal = $modal) => {
            setupHandleValidationError(
                modal,
                '.js-card-payment-modal-card-number-error',
                _t('seller_common:card-form.input.card-number.error.invalid'),
                event
            );
            stripeElementsCompleteState.cardNumberIsComplete = event.complete;
            if(isMultiPaymentModal()) {setMultiPaymentBuyButtonEnabledState(isMultiPaymentFormProbablyComplete())}
			toggleSubmitPaymentButton($modal);
        }

	);
	stripeCardElements['cardNumber'].mount(`${modalId} .js-card-payment-modal-card-number`);

	stripeCardElements['cardExpiry'] = elements.create('cardExpiry', { style });
	stripeCardElements['cardExpiry'].on('focus', () =>
		creditCardTracking.logFocus('cc-exp'),
	);
	stripeCardElements['cardExpiry'].on(
		'change',
        (event, modal = $modal) => {
            setupHandleValidationError(
                modal,
                '.js-card-payment-modal-expiry-date-error',
                _t('seller_common:card-form.input.card-expiry.error.invalid'),
                event
            );
            stripeElementsCompleteState.expiryDateIsComplete = event.complete;
            if(isMultiPaymentModal()) {setMultiPaymentBuyButtonEnabledState(isMultiPaymentFormProbablyComplete())}
			toggleSubmitPaymentButton($modal);
        }
	);
	stripeCardElements['cardExpiry'].mount(`${modalId} .js-card-payment-modal-expiry-date`);

	stripeCardElements['cardCvc'] = elements.create('cardCvc', { style });
	stripeCardElements['cardCvc'].on('focus', () => creditCardTracking.logFocus('cc-csc'));
	stripeCardElements['cardCvc'].on(
		'change',
        (event, modal = $modal) => {
            setupHandleValidationError(
                modal,
                '.js-card-payment-modal-cvc-error',
                _t('seller_common:card-form.input.card-cvc.error.invalid'),
                event
            );
            stripeElementsCompleteState.cvcIsComplete = event.complete;
            if(isMultiPaymentModal()) {setMultiPaymentBuyButtonEnabledState(isMultiPaymentFormProbablyComplete())}
			toggleSubmitPaymentButton($modal);
        }
	);
	stripeCardElements['cardCvc'].mount(`${modalId} .js-card-payment-modal-cvc`);

	togglePaymentRequestButton(elements, $modal, {
		enableWallets: options.enableWallets,
		paymentOptions: options.paymentOptions,
	});
};

const setupHandleValidationError = ($modal, selector, errorTextOverride, event) => {
	$modal.find(selector).addClass('d-none');
    const displayError = $modal.find(selector);
    if (event.error) {
        displayError.text(errorTextOverride || event.error.message).removeClass('d-none');
    } else {
        displayError.text('').addClass('d-none');
    }
};

const handleConfirmClick = ($modal, successCallback, override = null) => (event) => {
	const $button = $(event.target);
	if (!buttonIsDisabled($button) && !buttonIsLoading($button)) {
		confirmButtonSetLoadingState($modal, true, override);
		creditCardTracking.logSubmit();
		stripe
			.createToken(stripeCardElements['cardNumber'])
			.then((result) => {
				if (result.error) {
					const invalidCardElements = $modal
						.find('.StripeElement--invalid')
						.addClass('shake-animation');
					setTimeout(
						() => $modal.find('.StripeElement').removeClass('shake-animation'),
						1500,
					);
					const firstInvalidCardElementStripeName = invalidCardElements
						.first()
						.data('stripe-element-name');
					stripeCardElements[firstInvalidCardElementStripeName].focus();
				} else {
					$modal.modal('hide');
					$('.modal-backdrop').removeClass('modal-backdrop-blue');
					successCallback && successCallback(result.token);
				}
				confirmButtonSetLoadingState($modal, false, override);
			})
			.catch((err) => {
				showModalErrorText($modal, _t('seller_common:card-form.error.invalid-card'));
				confirmButtonSetLoadingState($modal, false, override);
				window.bugsnagClient.notify(
					new Error('Failed to create stripe card token' + ''),
					{ metaData: { err: err, errText: '' + err } },
				);
			});
	}
};

const handleSofortClick = ($modal, successCallback, override= null) => async (event) => {
    const $button = $(event.target);
    if (!buttonIsDisabled($button) && !buttonIsLoading($button)) {
        confirmButtonSetLoadingState($modal, true, override);
        creditCardTracking.logAction('sofort-payment-intent');
        let pid = $('.dashboard-project-details').data('project-id');

		try {
			const data = await initPaymentIntent('klarna', $modal.data('user-id'), $modal.data('plan-id'), $modal.data('n_responses'), $modal.data('code'), $modal.data('upsell'));

			let packType = $modal.data('pack-type')
			let pidParam = pid ? `&prx=${pid}` : ""
			let packTypeParam = packType ? `&packType=${packType}` : "";

			try {
				const result = await stripe.confirmKlarnaPayment(data.clientSecret, {
					payment_method: {
						billing_details: {
							email: window.Bark.USER.email,
							address: {
								country: $modal.data('country').toUpperCase(),
							},
						},
					},
					return_url: location.protocol + '//' + location.host + location.pathname + '?' + pidParam + packTypeParam,
				});

				if (result.error) {
					// Inform the customer that there was an error.
					creditCardTracking.logAction('payment failure', {
						payment_method: 'Klarna',
						stage: 'pre-confirm-stage'
					});
					showModalErrorText($modal, _t('seller_common:sofort-payment.error.invalid-request'));
				}
				confirmButtonSetLoadingState($modal, false, override);
			} catch (e) {
				sofortPaymentFailed($modal, e, override);
			}
		} catch (e) {
			sofortPaymentFailed($modal);
		}
    }
};

const sofortPaymentFailed = ($modal, e, override) => {
	Bark.hideLoading();
	showModalErrorText($modal, _t('seller_common:sofort-payment.error.invalid-request'));
	confirmButtonSetLoadingState($modal, false, override);
	window.bugsnagClient.notify(
		new Error('Failed to create stripe payment intent token' + ''),
		{ metaData: { err: e } },
	);
}

const handlePaymentClick = ($modal, successCallback, savedCardCallback = null) => (event) => {
    const paymentType = $modal.find('.js-payment-modal-confirm').attr('data-payment-method');
    lastAttemptedPaymentMethodType = paymentType;
    switch (paymentType) {
        case SAVED_CARD:
            savedCardCallback($modal, successCallback, '.js-payment-modal-confirm')(event);
            break;
        case NEW_CARD:
            handleConfirmClick($modal, successCallback, '.js-payment-modal-confirm')(event);
            break;
        case SOFORT_PAYMENT:
            handleSofortClick($modal, successCallback, '.js-payment-modal-confirm')(event);
            break;
        default:
            break;
    }
}

const handleCancelClick = ($modal, cancelCallback) => () => {
	creditCardTracking.logCancelClick();
	// if the user is closing the modal, we shouldn't remember it's state
	clearModal($modal);
	$modal.modal('hide');
	$('.modal-backdrop').removeClass('modal-backdrop-blue');
	cancelCallback && cancelCallback();
};

const handleCloseClick = ($modal, closeCallback) => {
	return () => {
		creditCardTracking.logClose();
		// if the user is closing the modal, we shouldn't remember it's state
		clearModal($modal);
		$modal.modal('hide');
		$('.modal-backdrop').removeClass('modal-backdrop-blue');
		closeCallback && closeCallback();
	};
};

/**
 * Only return the necessary trackable parameters
 * @returns {TrackableParameters}
 */
const filterOnlyTrackableParams = () => {
	const all = window.Bark.getTrackableParameters();
	const output = {
		trk_current_bes_token: all?.trk_current_bes_token || undefined
	};

	for (const [key, value] of Object.entries(all)) {
		if (key.startsWith('trk_estimated_') || key.startsWith('trk_metric_')) {
			output[key] = value;
		}
	}

	return output;
};

/**
 * @param {'klarna'|'apple_pay'|'google_pay'|'paypal'} paymentMethodType
 * @param {number} userId
 * @param {number} cpId
 * @param {number} n_responses
 * @param {string} code
 * @param {boolean} upsell
 * @returns {Promise<InitPaymentIntentResponse>}
 */
export const initPaymentIntent = (paymentMethodType, userId, cpId = null, n_responses = null, code = null, upsell = false) => {
    let payload = {}
    if (cpId) {
        payload = {cpId}
        if (code){
            payload.code = code
        }
    }
    if (n_responses) {
        payload = { n_responses}
    }
    if (upsell) {
        payload.upsell = upsell
    }
	payload.meta = filterOnlyTrackableParams();
    return new Promise((resolve, reject) => {
		try {
			console.log('Getting payment intent secret');
			barkApi(
				`user/${userId}/billing/prepare/${paymentMethodType}`,
				payload,
				(response) => {
					resolve({
						clientSecret: response?.data?.client_secret,
						price: response?.data?.price,
						nCredits: response?.data?.n_credits,
						nResponses: response?.data?.n_responses,
						invoiceId: response?.data?.invoice_id,
					});
				},
				() => {
					console.log(`Failed to get ${paymentMethodType} payment intent`);
					reject();
				}, 'POST'
			);
		} catch (e) {
			window.bugsnagClient.notify(new Error('Failed to get payment intent secret'), {
				metaData: { original: err },
			});
			reject();
		}
    });
};

/**
 * @param userId
 * @return {Promise<InitSetupIntentResponse>}
 */
export const initSetupIntent = (userId) => {
	return new Promise((resolve, reject) => {
		console.log('Getting setup intent id');
		barkApi(
			`user/${userId}/billing/prepare`,
			null,
			(response) => {
				setupIntent.id = response?.data?.setupIntentId;
				setupIntent.supportsIntents = response?.data?.supportsIntents;
				resolve({
					supportsIntents: setupIntent.supportsIntents,
					setupIntentId: setupIntent.id,
				});
			},
			() => {
				console.log('Failed to get setup intent');
				reject();
			},
		);
	}).catch((err) => {
		window.bugsnagClient.notify(new Error('Failed to get setup intent id'), {
			metaData: { original: err },
		});

		reject();
	});
};

/**
 * @param {int} userId
 * @param {string} tokenId
 * @return {Promise<confirmSetupIntentResponse>}
 */
export const confirmSetupIntent = (userId, tokenId) => {
	return new Promise(async (resolve, reject) => {
		try {
			const { supportsIntents, setupIntentId } = await initSetupIntent(userId);

			if (supportsIntents === false) {
				const result = await barkApi(
					'/seller/change-card/',
					{ token: tokenId },
					null,
					null,
					'POST',
				);

				if (result?.status) {
					resolve(false);
				} else {
					reject({ type: 'Fail', error: {} });
				}
			} else {
				const confirmation = await stripe.confirmCardSetup(setupIntentId, {
					payment_method: {
						card: { token: tokenId },
					},
				});

				if (confirmation.error) {
					reject({
						type: 'Stripe',
						error: confirmation.error,
					});
				} else {
					try {
						// This call is to try and prevent a race condition, especially in the Elite Pro call to
						//  ensure that we charge the correct card. As such, we can allow it to fail as a webhook also
						//  performs the same action
						setDefaultPaymentMethod(userId, confirmation.setupIntent.payment_method)
					} catch (e) {
						window.bugsnagClient.notify(
							new Error('Failed to mark this method as the default'),
							{
								metaData: { original: e },
							},
						);
					}

					resolve(confirmation.setupIntent);
				}
			}
		} catch (e) {
			reject({
				errorType: 'Error',
				error: e,
			});
		}
	});
};

/**
 * @param {'klarna'|'apple_pay'|'google_pay'|'paypal'} paymentMethodType
 * @param {string} paymentIntentId
 * @param {number} userId
 * @param successCallback
 * @returns {Promise<ConfirmPaymentIntentResponse>}
 */
export const confirmPaymentIntent = (paymentMethodType, paymentIntentId, userId, successCallback = null) => {
    return new Promise(async (resolve, reject) => {
        try {
            console.log('confirming payment intent secret', paymentIntentId);
            let response = await barkApi(
                `user/${userId}/billing/payment/${paymentMethodType}/verify-complete`,
                {
                    payment_intent_id: paymentIntentId,
                },
                null,
                null,
                'POST'
            );

            if (response?.status) {
                let resultPayload = {
                    invoice_id: response?.data?.invoice_id,
                    price: response?.data?.price,
                    credits: response?.data?.credits,
                    credit_balance: response?.data?.credit_balance,
                    cpId: response?.data?.cpId,
                    is_paid: response?.data?.is_paid,
                    n_responses: response?.data?.n_responses,
                    is_coupon_applied: response?.data?.is_coupon_applied,
                    vat_rate: response?.data?.vat_rate,
                    code: response?.data?.code
                };

                if (resultPayload.is_paid) {
                    successCallback && successCallback(resultPayload);
                }
                resolve({...resultPayload});
            } else {
                creditCardTracking.logAction('payment failure', {
                    payment_method: paymentMethodType,
                    stage: 'confirm-intent-stage'
                });
                console.log(`Failed to confirm ${paymentMethodType} payment intent`, e);
                window.bugsnagClient.notify(new Error('Failed to get payment intent secret'), {
                    metaData: {original: err},
                });
                reject({errorType: 'Fail', error: {}});
            }
        } catch (e) {
            reject({
                errorType: 'Error',
                error: e,
            });
        }
    });
}

export const setHeaderText = ($modal, headerText, override = null) => {
	$modal.find(override ?? '.js-card-modal-title-text').text(headerText);
};

export const setSubheaderHtml = ($modal, headerHtml, override = null) => {
	const subheaderHtmlElement = $modal.find(override ?? '.js-card-modal-subheader-html');
	subheaderHtmlElement.html(headerHtml);
	if (headerHtml) {
		subheaderHtmlElement.show();
	} else {
		subheaderHtmlElement.hide();
	}
};

export const setFormFooterHtml = ($modal, footerHtml) => {
	$modal.find('.js-form-footer-html').html(footerHtml);
	if (footerHtml) {
		$modal.find('.js-form-footer').show();
	} else {
        $modal.find('.js-form-footer').hide();
	}
};

export const setConfirmText = ($modal, confirmText, override = null) => {
	$modal.find(override ?? '.js-card-modal-confirm-text').text(confirmText);
};

export const setConfirmPaymentIcon = ($modal, iconHtml, override = null) => {
    $modal.find(override ?? '.js-payment-modal-confirm-text').find('.icon').html(iconHtml);
};

export const setPaymentConfirmOption = ($modal, data) => {
    $modal.find('.js-payment-modal-confirm').attr(data);
};

export const setCancelText = ($modal, cancelText) => {
	$modal.find('.js-card-modal-cancel-text').text(cancelText);
};

export const showModalErrorText = ($modal, errorText) => {
	$modal.find('.js-payment-error-field').text(errorText).removeClass('d-none');
};

export const hideModalErrorText = ($modal) => {
	$modal.find('.js-payment-error-field').text('').addClass('d-none');
};

export const taxWord = ($modal) => {
	return $modal.data('tax-word');
};

export const hasTax = ($modal) => {
	return $modal.data('has-tax');
};

export const getPaymentInfo = async (userId, paymentIntentId) => {
	return await barkApi(`/user/${userId}/billing/payment/${paymentIntentId}`);
};

export const retryIntent = async (intentSecret, paymentMethodId, tokenId) => {
	creditCardTracking.logAction('retry intent');

	const payload = paymentMethodId ? paymentMethodId : { card: { token: tokenId } };
	const result = await stripe.confirmCardPayment(intentSecret, {
		payment_method: payload,
		setup_future_usage: 'off_session',
	});

	if (result.error) {
		if (result.error.type === 'action_required') {
			let result;
			let error;
			await promptUserAction(
				intentSecret,
				(res) => {
					if (res.paymentIntent) {
						result = res;
					} else {
						error = res.error;
					}
				},
				(err) => {
					error = err;
				},
			);

			if (error) {
				creditCardTracking.logAction('retry intent - fail');

				const exception = new Error('Failed to confirm payment');
				exception.original = error;
				throw exception;
			}

			creditCardTracking.logAction('retry intent - success');
			return result;
		} else {
			creditCardTracking.logAction('retry intent - fail');
			throw new Error('Failed to process payment');
		}
	} else {
		creditCardTracking.logAction('retry intent - success');
		return result;
	}
};

const setMultiPaymentBuyButtonEnabledState = (isEnabled = false, $overrideSelector = null) => {
    const $selector = $overrideSelector ?? $('.js-payment-modal-confirm');
    if(isEnabled) {
        $selector.removeClass('disabled');
    } else {
        $selector.addClass('disabled');
    }
}

/**
 * isMultiPaymentFormProbablyComplete - Returns true if all fields have data and are the
 *                                      right length. Doesn't guarantee that the values are valid.
 * @returns {boolean}
 */
const isMultiPaymentFormProbablyComplete = () => {
    if(stripeElementsCompleteState === {}){
        return false;
    }
    let elementsComplete = true;
    Object.values(stripeElementsCompleteState).forEach((value) => {
        elementsComplete = elementsComplete && value;
    })
    return elementsComplete;
}

const resetStripeElementCompleteState = () => {
    stripeElementsCompleteState.cardNumberIsComplete = false;
    stripeElementsCompleteState.expiryDateIsComplete = false;
    stripeElementsCompleteState.cvcIsComplete = false;
}

const setFormStateAfterFailedSCAPayment = () => {

    if(lastAttemptedPaymentMethodType === NEW_CARD) {
        stripeCardElements['cardCvc']?.clear();
        stripeElementsCompleteState.cardNumberIsComplete = true;
        stripeElementsCompleteState.expiryDateIsComplete = true;
        stripeElementsCompleteState.cvcIsComplete = false;
    }

    if(lastAttemptedPaymentMethodType === SAVED_CARD) {
        stripeCardElements['cardCvc']?.clear();
        stripeCardElements['cardNumber']?.clear();
        stripeCardElements['cardExpiry']?.clear();
        resetStripeElementCompleteState();
        setMultiPaymentBuyButtonEnabledState(true);
    }

    // We shouldn't get SCA with Sofort, but just in case...
    if(lastAttemptedPaymentMethodType === SOFORT_PAYMENT) {
        stripeCardElements['cardCvc']?.clear();
        stripeCardElements['cardNumber']?.clear();
        stripeCardElements['cardExpiry']?.clear();
        resetStripeElementCompleteState();
        setMultiPaymentBuyButtonEnabledState(true);
    }

    setActiveAccordionSection(lastAttemptedPaymentMethodType ?? NEW_CARD)
}

export const BarkPaymentsCardModal = {
	initCardModal,
    initMultiPaymentCardModal,
    showCardModal,
    showMultiPaymentModal,
	promptUserAction,
	clearModal,
	setHeaderText,
	setSubheaderHtml,
	setFormFooterHtml,
	setConfirmText,
	setCancelText,
    setConfirmPaymentIcon,
	showModalErrorText,
	hideModalErrorText,
	taxWord,
	hasTax,
	confirmSetupIntent,
	retryIntent,
	getPaymentInfo,
    initPaymentIntent,
	confirmPaymentIntent,
    setPaymentConfirmOption,
    confirmButtonSetLoadingState,
    getActiveAccordionSection
};

export const exposeCreditCardModalToWindow = () => {
	window.BarkPaymentsCardModal = BarkPaymentsCardModal;
};
